import React, { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Check, X } from "lucide-react";
import { useAiPolish } from "@/hooks/useAiPolish";
import { useAiSuggestionLog, AiSuggestionTarget } from "@/hooks/useAiSuggestionLog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface AiPolishInlineProps extends AiSuggestionTarget {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  context?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * Kompakt textruta med en liten AI-knapp i hörnet. Används där fältet
 * saknar egen rubrik, t.ex. i listor med risker och åtgärder.
 */
export const AiPolishInline: React.FC<AiPolishInlineProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  context,
  className,
  disabled = false,
  id,
  documentType,
  documentId,
  projectId,
  fieldKey,
  fieldLabel,
}) => {
  const { polish, isPolishing } = useAiPolish();
  const { logSuggestion, markAccepted } = useAiSuggestionLog();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [logId, setLogId] = useState<string | null>(null);

  const handlePolish = async () => {
    const original = value;
    const result = await polish(value, context ?? fieldLabel);
    if (!result) return;
    if (!result.changed) {
      toast.success("Texten ser redan bra ut");
      return;
    }
    setSuggestion(result.polished);
    setLogId(
      await logSuggestion(
        { documentType, documentId, projectId, fieldKey, fieldLabel },
        original,
        result.polished,
      ),
    );
  };

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled || isPolishing}
          className={cn("pr-9", className)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title="Snygga till med AI"
          aria-label="Snygga till med AI"
          onClick={handlePolish}
          disabled={disabled || isPolishing || !value.trim()}
          className="absolute right-1 top-1 h-7 w-7 text-muted-foreground hover:text-primary"
        >
          {isPolishing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {suggestion !== null && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-2 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">AI-förslag</p>
          <p className="text-sm whitespace-pre-wrap">{suggestion}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              className="h-7"
              onClick={() => {
                onChange(suggestion);
                setSuggestion(null);
                void markAccepted(logId);
                onBlur?.();
              }}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Använd
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => setSuggestion(null)}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Behåll min text
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPolishInline;
